// Utility functions for package-based measurement system

export interface PackageProductData {
  measurement_unit: string;
  price_per_unit: number;
  package_coverage_per_unit?: number;
  minimum_packages?: number;
  min_quantity: number;
  max_quantity?: number;
  step_quantity: number;
}

export interface PackageCalculation {
  pricePerMeter: number;
  packagePrice: number;
  coveragePerPackage: number;
  minimumPackages: number;
  totalCoverage: number;
  totalPrice: number;
}

/**
 * Check if a product uses package-based measurement system
 */
export const isPackageBasedProduct = (measurementUnit: string): boolean => {
  return measurementUnit === 'package_by_meter' || measurementUnit === 'package_by_square_meter';
};

/**
 * Get the base unit for package-based products (meter or m²)
 */
export const getPackageBaseUnit = (measurementUnit: string): string => {
  switch (measurementUnit) {
    case 'package_by_meter':
      return 'מטר';
    case 'package_by_square_meter':
      return 'מ"ר';
    default:
      return '';
  }
};

/**
 * Calculate package-based pricing and coverage
 */
export const calculatePackageMetrics = (
  product: PackageProductData,
  quantityInPackages: number
): PackageCalculation | null => {
  if (!isPackageBasedProduct(product.measurement_unit)) {
    return null;
  }

  const pricePerMeter = product.price_per_unit;
  const coveragePerPackage = product.package_coverage_per_unit || 1;
  const packagePrice = pricePerMeter * coveragePerPackage;
  const minimumPackages = product.minimum_packages || product.min_quantity;
  const totalCoverage = quantityInPackages * coveragePerPackage;
  const totalPrice = quantityInPackages * packagePrice;

  return {
    pricePerMeter,
    packagePrice,
    coveragePerPackage,
    minimumPackages,
    totalCoverage,
    totalPrice
  };
};

/**
 * Validate package quantity against constraints
 */
export const validatePackageQuantity = (
  product: PackageProductData,
  quantity: number
): { isValid: boolean; message?: string } => {
  if (!isPackageBasedProduct(product.measurement_unit)) {
    return { isValid: true };
  }

  const minimumPackages = product.minimum_packages || product.min_quantity;

  if (quantity < minimumPackages) {
    return {
      isValid: false,
      message: `מינימום ${minimumPackages} חבילות נדרש`
    };
  }

  if (product.max_quantity && quantity > product.max_quantity) {
    return {
      isValid: false,
      message: `מקסימום ${product.max_quantity} חבילות מותר`
    };
  }

  if (quantity % product.step_quantity !== 0) {
    return {
      isValid: false,
      message: `כמות חייבת להיות בכפולות של ${product.step_quantity}`
    };
  }

  return { isValid: true };
};

/**
 * Format package-based display text
 */
export const formatPackageDisplay = (
  product: PackageProductData,
  showFullDetails: boolean = true
): {
  unitText: string;
  priceText: string;
  detailsText?: string;
} => {
  if (!isPackageBasedProduct(product.measurement_unit)) {
    return {
      unitText: '',
      priceText: '',
    };
  }

  const baseUnit = getPackageBaseUnit(product.measurement_unit);
  const packagePrice = product.price_per_unit * (product.package_coverage_per_unit || 1);
  const coverage = product.package_coverage_per_unit || 1;

  const unitText = 'חבילה/יחידה';
  const priceText = `₪${packagePrice.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} לחבילה/יחידה`;

  if (showFullDetails) {
    const detailsText = `₪${product.price_per_unit.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ל${baseUnit} | ${coverage} ${baseUnit} בחבילה/יחידה`;
    return { unitText, priceText, detailsText };
  }

  return { unitText, priceText };
};

/**
 * Format package coverage display
 */
export const formatPackageCoverage = (
  totalCoverage: number,
  measurementUnit: string
): string => {
  const baseUnit = getPackageBaseUnit(measurementUnit);
  return `${totalCoverage.toLocaleString('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${baseUnit}`;
};